/*
 * WW Sigma Pre-Order — CTA add-to-cart + sold-out waitlist popup + Klaviyo dual-write
 * Author: Plaud Tayler
 * Version: 1.1.0 - 2026-08-27 11:20 (UTC+8) - Plaud Tayler — waitlist Klaviyo payload carries the selected country as the custom profile property "Country" (self-declared; distinct from Klaviyo's IP-derived location, which real traffic and later orders keep overwriting)
 * Version-note: 1.0.0 - 2026-07-29 16:10 (UTC+8) - Plaud Tayler — pre-order mechanism redesign:
 * in-stock CTA = AJAX /cart/add.js (native GET link is the no-JS fallback); sold-out is
 * detected at CLICK TIME via /products/{handle}.js variant.available (this store's /cart/add
 * returns 200 even at DENY+0 stock — inventory enforced only at checkout; 422 branch kept as
 * redundant guard) → every CTA flips to waitlist mode and the popup opens; waitlist form fires
 * the Klaviyo client-subscribe (keepalive) BEFORE the native Shopify customer POST proceeds —
 * both stores receive the signup; ?waitlist=joined re-opens the popup in success state.
 * Document-delegated listeners survive hot-reloads. ES5.
 */
(function () {
  if (window.__wwSigmaPreorder) return;
  window.__wwSigmaPreorder = true;

  /* ---- popup open / close (same interaction contract as the hero YouTube modal) ---- */

  var lastTrigger = null;
  var releaseTrap = null;

  function popup() { return document.querySelector('[data-wlpop]'); }

  function openPopup(trigger, joined) {
    var pop = popup();
    if (!pop) return;
    if (joined) {
      pop.classList.add('is-joined');
      var ok = pop.querySelector('[data-wlpop-success]');
      if (ok) ok.hidden = false;
    }
    pop.hidden = false;
    document.documentElement.classList.add('ww-sigma-hero-modal-open'); /* shared scroll-lock */
    lastTrigger = trigger || null;
    var focusEl = pop.querySelector(joined ? '.ww-sigma-wlpop__close' : '.ww-sigma-wlpop__input') || pop.querySelector('.ww-sigma-wlpop__close');
    if (focusEl) focusEl.focus();
    /* Tab cycling locked inside the dialog (reuses global.js trapFocus, W2 fix); trapFocus focuses
       the first focusable element, deliberately called AFTER the business focus above — when the
       joined-state intent to focus close gets overridden by the trap's first element (the input),
       the trap wins (keyboard users starting at the form is more sensible) - Plaud Tayler - 2026-08-05 18:10 */
    if (typeof trapFocus === 'function') releaseTrap = trapFocus(pop);
  }

  function closePopup() {
    var pop = popup();
    if (!pop) return;
    pop.hidden = true;
    document.documentElement.classList.remove('ww-sigma-hero-modal-open');
    if (typeof releaseTrap === 'function') { releaseTrap(); releaseTrap = null; }
    /* :target no-JS path leaves the fragment behind — clear it so re-close works */
    if (window.location.hash === '#waitlist-popup' && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (lastTrigger) { lastTrigger.focus(); lastTrigger = null; }
  }

  /* ---- sold-out flip: swap every pre-order CTA to waitlist mode in place ---- */

  function flipToWaitlist() {
    var pop = popup();
    var label = (pop && pop.getAttribute('data-soldout-label')) || 'Join the Waitlist';
    var ctas = document.querySelectorAll('[data-preorder-add]');
    Array.prototype.forEach.call(ctas, function (a) {
      a.removeAttribute('data-preorder-add');
      a.setAttribute('data-wlpop-open', '');
      a.setAttribute('href', '#waitlist-popup');
      /* hero CTA carries a bare text node + icon; actionbar wraps the text in a span */
      var span = a.querySelector('.ww-sigma-actionbar__label');
      if (span) { span.textContent = label; }
      else {
        var icon = a.querySelector('svg');
        if (icon) icon.parentNode.removeChild(icon);
        a.textContent = label;
      }
    });
  }

  /* ---- add to cart (AJAX over the native GET link) ----
     Falsified by live test (2026-07-29, plaud-ai-sandbox): this store's /cart/add returns 200 even
     at DENY + 0 stock (inventory is only enforced at checkout), so 422 cannot be relied on.
     Sold-out detection moved to reading variant.available from /products/{handle}.js at click time
     (measured propagation is second-level); the 422 branch is kept as a redundant guard. */

  var adding = false;

  function soldOutFlip(link) {
    adding = false;
    link.style.opacity = '';
    flipToWaitlist();
    openPopup(link, false);
  }

  function postAdd(link, id) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: 1 })
    }).then(function (res) {
      if (res.ok) { window.location.href = '/cart'; return; }
      if (res.status === 422) { soldOutFlip(link); return; }
      window.location.href = link.href; /* unexpected status → native GET fallback */
    }).catch(function () {
      window.location.href = link.href; /* network error → native fallback */
    });
  }

  function addToCart(link) {
    if (adding) return;
    adding = true;
    link.style.opacity = '0.7';
    var id = parseInt(link.getAttribute('data-variant-id'), 10);
    var handle = link.getAttribute('data-product-handle');
    if (!handle) { postAdd(link, id); return; }
    /* inventory ground-truth check at click time: the cached-page race condition closes here */
    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (product) {
        if (product && product.variants) {
          for (var i = 0; i < product.variants.length; i++) {
            if (product.variants[i].id === id) {
              if (!product.variants[i].available) { soldOutFlip(link); return; }
              break;
            }
          }
        }
        postAdd(link, id); /* available, or product data unavailable (degrade to optimistic add) */
      })
      .catch(function () { postAdd(link, id); });
  }

  /* ---- Klaviyo dual-write: fire-and-forget before the native customer POST ---- */

  function klaviyoSubscribe(form) {
    var wrap = form.closest('[data-wlpop-formwrap]');
    if (!wrap) return;
    var key = wrap.getAttribute('data-klaviyo-key');
    var list = wrap.getAttribute('data-klaviyo-list');
    var emailEl = form.querySelector('input[type="email"]');
    if (!key || !list || !emailEl || !emailEl.value) return;
    var profileAttrs = { email: emailEl.value };
    /* the form's country select previously reached only the Shopify leg (defaultAddress.country);
       Klaviyo filled its own location from the request IP, so a DE-store signup picking Singapore
       read as Germany. Stored as a CUSTOM property (not location.country): location stays
       Klaviyo-managed, "Country" = what the user declared. */
    var countryEl = form.querySelector('select[name="contact[Country]"]');
    if (countryEl && countryEl.value) {
      profileAttrs.properties = { Country: countryEl.value };
    }
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: wrap.getAttribute('data-klaviyo-source') || 'plaud-one-preorder-waitlist',
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: list } } }
      }
    };
    try {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          'accept': 'application/vnd.api+json',
          'revision': '2025-10-15'
        },
        keepalive: true, /* survives the form's full-page navigation */
        body: JSON.stringify(payload)
      }).catch(function () {});
    } catch (e) { /* Klaviyo failure must not block the primary Shopify store write */ }
  }

  /* ---- delegated wiring ---- */

  document.addEventListener('click', function (e) {
    var add = e.target.closest && e.target.closest('[data-preorder-add]');
    if (add) { e.preventDefault(); addToCart(add); return; }
    var open = e.target.closest && e.target.closest('[data-wlpop-open]');
    if (open) { e.preventDefault(); openPopup(open, false); return; }
    var close = e.target.closest && e.target.closest('[data-wlpop-close]');
    if (close) { closePopup(); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var pop = popup();
    if (pop && !pop.hidden) closePopup();
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.classList && form.classList.contains('ww-sigma-wlpop__form')) {
      klaviyoSubscribe(form); /* no preventDefault — the native POST continues to Shopify customer */
      var btn = form.querySelector('.ww-sigma-wlpop__btn');
      if (btn) btn.disabled = true;
    }
  });

  /* ---- success round-trip: return_to lands back here with ?waitlist=joined ---- */

  function initJoined() {
    if (window.location.search.indexOf('waitlist=joined') === -1) return;
    openPopup(null, true);
  }
  if (document.readyState !== 'loading') initJoined();
  else document.addEventListener('DOMContentLoaded', initJoined);
})();
